/**
 * ClientGroupManagerModal
 *
 * Displays a modal with:
 * - AG Grid listing all client groups
 * - Text input and add button to create new group
 * - Action column to edit/delete groups
 */

import React, { useMemo, useState } from "react";
import styles from "./ClientGroupManagerModal.module.css";
import { Modal, Form, Row, Col } from "react-bootstrap";
import { ColDef } from "ag-grid-community";
import {
  addGroup,
  updateGroup,
  deleteGroup,
} from "./ClientGroupManagerModal.service";
import CustomButton from "../../../../../pages/reusable_components/custom_button_component/Button";
import * as Icon from "react-bootstrap-icons";
import CygnusGrid from "../../../../../pages/reusable_components/CygnusGrid/CygnusGrid";

const ClientGroupManagerModal: React.FC<{
  show: boolean;
  onHide: () => void;
  groupData: [];
  refreshGroups: () => void;
}> = ({ show, onHide, groupData, refreshGroups }) => {
  const [groupName, setGroupName] = useState<string>("");
  const gridStyle = useMemo(() => ({ height: "55vh", width: "100%" }), []);

  const columnDefs: ColDef[] = [
    { field: "ClientGroupName", headerName: "Group Name", flex: 1 },
    {
      headerName: "Action",
      field: "",
      cellRenderer: (params: any) => {
        return (
          <div className={styles.actionContainer}>
            <div className={styles.widthFit}>
              <Icon.PencilSquare
                className={styles.headerEyeIcon}
                onClick={() => {
                  const newName = prompt(
                    "Enter new group name:",
                    params.data.ClientGroupName
                  );
                  if (newName) {
                    updateGroup(params.data.ClientGroupId, newName).then(() =>
                      refreshGroups()
                    );
                  }
                }}
              />
            </div>{" "}
            <div className={styles.widthFit}>
              <Icon.Trash
                className={styles.headerDeleteIcon}
                onClick={() => {
                  if (
                    window.confirm(
                      "Are you sure you want to delete this group?"
                    )
                  ) {
                    deleteGroup(params.data.ClientGroupId).then(() =>
                      refreshGroups()
                    );
                  }
                }}
              />
            </div>
          </div>
        );
      },
    },
  ];

  const handleAdd = async () => {
    if (groupName.trim()) {
      await addGroup(groupName);
      setGroupName("");
      refreshGroups();
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered keyboard={false}>
      <Modal.Header closeButton>
        <Modal.Title>Manage Client Groups</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Row className="mb-3">
            <Col>
              <Form.Control
                placeholder="Enter new group name"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                className={"custom-input"}
              />
            </Col>
            <Col xs="auto">
              <CustomButton onclick={handleAdd} content={"Add Group"} />
            </Col>
          </Row>
        </Form>
        <div className="ag-theme-balham" style={gridStyle}>
          <CygnusGrid
            columnDefs={columnDefs}
            fetchData={async () => {
              // Since this is not paginated, just return everything
              return { records: groupData, totalCount: groupData.length };
            }}
            rowModelType="clientSide"
            rowSelection={undefined}
            dataIdentifier={""}
            // cachedData={groupData}
          />
        </div>{" "}
      </Modal.Body>
    </Modal>
  );
};

export default ClientGroupManagerModal;
